import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Card, Spinner } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FadeInLeft, FadeInRight, ScrollFadeIn } from './MotionWrappers';
import Nav from './Nav';
import SEO from './SEO';
import TryOn from './TryOn';
import SignInModal from './SignInModal';
import { useCurrency } from '../context/CurrencyContext';
import { supabase } from '../supabaseClient';
import apiClient from '../config/config';

function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { currency, getCurrencySymbol } = useCurrency();
  
  const [product, setProduct] = useState(null);
  const [brandProducts, setBrandProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [savedProducts, setSavedProducts] = useState([]);
  const [savedBrands, setSavedBrands] = useState([]);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInModalMessage, setSignInModalMessage] = useState('');
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [tryOnImage, setTryOnImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [authEmail, setAuthEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');

  const normalizeProductId = (productId) => {
    if (productId === null || productId === undefined) return '';
    return String(productId);
  };

  // Helper function to properly capitalize product titles (title case)
  const capitalizeTitle = (title) => {
    if (!title) return '';
    
    // Words that should remain lowercase in title case
    const lowerCaseWords = ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'from', 'by', 'with', 'as'];
    
    return title
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        // Always capitalize first word and words not in the lowerCaseWords list
        if (index === 0 || !lowerCaseWords.includes(word)) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return word; // Keep lowercase words lowercase
      })
      .join(' ');
  };

  // Fetch user session and saved products/brands
  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      if (session?.user) {
        const { data } = await supabase
          .from('saved_products')
          .select('product_id')
          .eq('user_id', session.user.id);
        if (data) {
          setSavedProducts(data.map(item => normalizeProductId(item.product_id)));
        }
        // Load saved brands
        const { data: brandsData } = await supabase
          .from('saved_brands')
          .select('brand_name')
          .eq('user_id', session.user.id);
        if (brandsData) {
          setSavedBrands(brandsData.map(item => item.brand_name));
        }
      }
    };
    getSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const toggleSaveProduct = async (productId) => {
    if (!user) {
      setSignInModalMessage('Sign in to save your favorite product.');
      setShowSignInModal(true);
      return;
    }
    const normalizedProductId = normalizeProductId(productId);
    try {
      if (savedProducts.includes(normalizedProductId)) {
        await supabase
          .from('saved_products')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', normalizedProductId);
        setSavedProducts(savedProducts.filter(id => id !== normalizedProductId));
      } else {
        await supabase
          .from('saved_products')
          .insert({ user_id: user.id, product_id: normalizedProductId });
        setSavedProducts([...savedProducts, normalizedProductId]);
      }
    } catch (error) {
      console.error('Error toggling saved product:', error);
    }
  };

  const toggleSaveBrand = async (brandName) => {
    if (!user) {
      setSignInModalMessage('Sign in to bookmark your favorite brands!');
      setShowSignInModal(true);
      return;
    }
    const isSaved = savedBrands.includes(brandName);
    try {
      if (isSaved) {
        await supabase
          .from('saved_brands')
          .delete()
          .eq('user_id', user.id)
          .eq('brand_name', brandName);
        setSavedBrands(savedBrands.filter(b => b !== brandName));
      } else {
        await supabase
          .from('saved_brands')
          .insert({ user_id: user.id, brand_name: brandName });
        setSavedBrands([...savedBrands, brandName]);
      }
    } catch (error) {
      console.error('Error toggling saved brand:', error);
    }
  };

  const handleTryOn = (imageUrl) => {
    if (!user) {
      setSignInModalMessage('Sign in to try it on and save your look.');
      setShowSignInModal(true);
      return;
    }
    setTryOnImage(imageUrl);
    setTryOnOpen(true);
  };

  const handleGoogleSignIn = async () => {
    setAuthLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.href }
    });
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setShowSignInModal(false);
    }
    setAuthLoading(false);
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    const { error } = await supabase.auth.signInWithOtp({ email: authEmail });
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email for the login link!');
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignIn = async (email, password) => {
    setAuthLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setShowSignInModal(false);
    }
    setAuthLoading(false);
  };
  const getSnippet = (text) => {
    if (!text) return '';
    // Splits by first emoji using regex and takes the first part
    return text.split(/[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u)[0];
  };


  const handleEmailPasswordSignUp = async (email, password) => {
    setAuthLoading(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { user_role: 'general' },
      },
    });
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email for the confirmation link!');
    }
    setAuthLoading(false);
  };

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(`/products/${productId}/?currency=${currency}`);
        setProduct(response.data);
        setSelectedImageIndex(0);
        
        // Fetch more products from the same brand
        if (response.data.brand) {
          const brandResponse = await apiClient.get(`/products/search/?brand=${encodeURIComponent(response.data.brand)}&currency=${currency}`);
          // Filter out the current product
          const otherProducts = brandResponse.data.filter(p => 
            (p.id !== response.data.id) && (p.link !== response.data.link)
          );
          setBrandProducts(otherProducts.slice(0, 10));
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId, currency]);

  if (loading) {
    return (
      <>
        <Container className="py-5">
          <Nav />
          <div className="text-center">
            <Spinner animation="border" />
            <p className="mt-3">Loading product...</p>
          </div>
        </Container>
      </>
    );
  }

  if (error || !product) {
    return (
      <>
        <Container className="py-5">
          <Nav />
          <div className="text-center">
            <h2>Product not found</h2>
            <Button variant="dark" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </div>
        </Container>
      </>
    );
  }

  const productJsonLd = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: capitalizeTitle(product.title),
    image: product.image || '',
    description: product.description || `${capitalizeTitle(product.title)} by ${product.brand} — authentic Kashmiri fashion available on Yemberzal.`,
    brand: {
      '@type': 'Brand',
      name: product.brand || 'Kashmiri Brand',
    },
    ...(product.price && {
      offers: {
        '@type': 'Offer',
        url: `https://theyemberzal.com/product/${product.id}`,
        priceCurrency: product.currency || 'INR',
        price: product.price,
        availability: 'https://schema.org/InStock',
      },
    }),
  } : null;

  return (
    <>
      {product && (
        <SEO
          title={`${capitalizeTitle(product.title)} by ${product.brand}`}
          description={product.description ? product.description.slice(0, 155) : `Shop ${capitalizeTitle(product.title)} by ${product.brand}. Authentic Kashmiri fashion with AI virtual try-on on Yemberzal.`}
          path={`/product/${product.id}`}
          image={product.image}
          type="product"
          jsonLd={productJsonLd}
        />
      )}
      <Container className="py-5">
        <Nav />
        {/* Back Button */}
        <Button 
          variant="link" 
          onClick={() => navigate(-1)}
          style={{ 
            color: '#1a1a1a', 
            textDecoration: 'none', 
            marginBottom: '20px',
            padding: 0,
            fontSize: '16px'
          }}
        >
          ← Back
        </Button>

        {/* Product Details */}
        <Row>
          {/* Product Images */}
          <Col md={6} className="mb-4">
            <FadeInLeft delay={0.1}>
            {(() => {
              const allImages = [product.image, ...(product.additional_images || [])].filter(Boolean);
              const selectedImage = allImages[selectedImageIndex] || product.image;
              return (
                <div style={{ display: 'flex', gap: '12px' }}>
                  {/* Thumbnail Strip (left side) */}
                  {allImages.length > 1 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      flexShrink: 0,
                    }}>
                      {allImages.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImageIndex(index)}
                          style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            border: selectedImageIndex === index
                              ? '2px solid #1a1a1a'
                              : '2px solid transparent',
                            padding: 0,
                            cursor: 'pointer',
                            background: '#f5f5f5',
                            opacity: selectedImageIndex === index ? 1 : 0.6,
                            transition: 'all 0.2s',
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                          onMouseLeave={(e) => {
                            if (selectedImageIndex !== index) e.currentTarget.style.opacity = '0.6';
                          }}
                        >
                          <img
                            src={img}
                            alt={`${product.title} ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = '/default-image.avif';
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Main Image */}
                  <div className="glass-card" style={{
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    backgroundColor: 'rgba(248,248,248,0.7)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    position: 'relative',
                    flex: 1,
                    minWidth: 0,
                  }}>
                    <img
                      src={selectedImage}
                      alt={product.title}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '600px',
                        objectFit: 'contain',
                        objectPosition: 'center'
                      }}
                      onError={(e) => {
                        console.error('Image failed to load:', selectedImage);
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/default-image.avif';
                      }}
                    />
                    {/* Heart Save Icon */}
                    <button
                      style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: 'rgba(255,255,255,0.95)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '44px',
                        height: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!user) {
                          setShowSignInModal(true);
                          return;
                        }
                        toggleSaveProduct(product.id ?? product.link);
                      }}
                      title={savedProducts.includes(normalizeProductId(product.id ?? product.link)) ? 'Remove from saved' : 'Save product'}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill={savedProducts.includes(normalizeProductId(product.id ?? product.link)) ? '#e63946' : 'none'}
                        stroke="#e63946"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 21s-7-4.35-7-10A5 5 0 0 1 12 6a5 5 0 0 1 7 5c0 5.65-7 10-7 10z" />
                      </svg>
                    </button>
                    {/* Try it on Button */}
                    <button
                      style={{
                        position: 'absolute',
                        bottom: '16px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        padding: '10px 20px',
                        borderRadius: '999px',
                        border: 'none',
                        background: 'rgba(0,0,0,0.7)',
                        color: '#fff',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        backdropFilter: 'blur(6px)',
                      }}
                      onClick={(e) => { e.stopPropagation(); handleTryOn(selectedImage); }}
                    >
                      Try it on
                    </button>
                  </div>
                </div>
              );
            })()}
            </FadeInLeft>
          </Col>

          {/* Product Info */}
          <Col md={6}>
            <FadeInRight delay={0.2}>
            <div style={{ padding: '20px 0' }}>
              {/* Brand with link and bookmark icon */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                marginBottom: '8px'
              }}>
                <span 
                  className="yb-brand-name"
                  onClick={() => navigate(`/brand/${encodeURIComponent(product.brand)}`)}
                  style={{ 
                    fontSize: '14px', 
                    color: '#888', 
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#1a1a1a'}
                  onMouseLeave={(e) => e.target.style.color = '#888'}
                >
                  {product.brand || 'Unknown Brand'}
                </span>
                {/* Bookmark Icon for Brand */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSaveBrand(product.brand);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  title={savedBrands.includes(product.brand) ? 'Remove from saved' : 'Save brand'}
                >
                  <svg 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24" 
                    fill={savedBrands.includes(product.brand) ? '#1a1a1a' : 'none'}
                    stroke="#1a1a1a" 
                    strokeWidth="2"
                  >
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                  </svg>
                </button>
              </div>

              {/* Title */}
              <h1 style={{ 
                fontSize: '28px', 
                fontWeight: '600',
                marginBottom: '16px',
                color: '#222'
              }}>
                {capitalizeTitle(product.title) || 'Unnamed Product'}
              </h1>

              {/* Out of stock badge */}
              {product.is_out_of_stock && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#dc2626',
                  fontSize: '11px',
                  fontWeight: '700',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  padding: '6px 14px',
                  borderRadius: '4px',
                  marginBottom: '20px',
                  fontFamily: 'Montserrat, sans-serif',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Out of Stock
                </div>
              )}

              {/* Price */}
              <p style={{ 
                fontSize: '24px', 
                fontWeight: '700',
                color: '#1a1a1a',
                marginBottom: '24px'
              }}>
                {getCurrencySymbol()} {product.price ? Number(product.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
              </p>

              {/* Category Labels */}
              {product.category && product.category !== 'uncategorized' && (
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px', 
                  marginBottom: '24px'
                }}>
                  {product.category.split(/, | & /).filter(cat => cat.trim()).map((cat, index) => (
                    <span
                      key={index}
                      className="yb-category-tag"
                      style={{
                        backgroundColor: '#f0f0f0',
                        color: '#1a1a1a',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      {cat}
                    </span>
                  ))}
                </div>
              )}

              {/* Description */}
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  Description
                </h3>

              <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.7' }}>
                {product.description ? getSnippet(product.description) : ''} 
              </p>
                {/* <p style={{ 
                  fontSize: '15px', 
                  color: '#555', 
                  lineHeight: '1.7'
                }}>
                  {product.description || ''}
                </p> */}
              </div>

              {/* Purchase Button */}
              <Button
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: '#1a1a1a',
                  border: 'none',
                  padding: '16px 40px',
                  fontSize: '16px',
                  fontWeight: '600',
                  borderRadius: '50px',
                  width: '100%',
                  maxWidth: '300px'
                }}
              >
                <span className="yb-brand-name text-white">Purchase from {product.brand || 'Brand'}</span>
              </Button>
            </div>
            </FadeInRight>
          </Col>
        </Row>

        {/* More Products from Brand */}
        {brandProducts.length > 0 && (
          <ScrollFadeIn>
          <div style={{ marginTop: '60px' }}>
            <h2 className="yb-brand-name" style={{ fontSize: '22px', fontWeight: '600', marginBottom: '24px' }}>
              More from {product.brand}
            </h2>
            
            <div style={{
              display: 'flex',
              overflowX: 'auto',
              gap: '16px',
              paddingBottom: '20px',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch'
            }}>
              {brandProducts.map((brandProduct, index) => (
                <div
                  key={index}
                  onClick={() => navigate(`/product/${brandProduct.id || encodeURIComponent(brandProduct.link)}`)}
                  style={{
                    minWidth: '200px',
                    maxWidth: '200px',
                    cursor: 'pointer',
                    scrollSnapAlign: 'start',
                    flexShrink: 0
                  }}
                >
                  {/* Image */}
                  <div style={{
                    width: '100%',
                    height: '400px',
                    overflow: 'hidden',
                    borderRadius: '4px',
                    backgroundColor: '#f5f5f5',
                    position: 'relative',
                    marginBottom: '12px'
                  }}>
                    <img
                      src={brandProduct.image || '/default-image.avif'}
                      alt={brandProduct.title || 'Product Image'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center top',
                        transition: 'transform 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      onError={(e) => {
                        console.error('Brand product image failed to load:', brandProduct.image);
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/default-image.avif';
                      }}
                    />

                    {/* Heart Save Icon */}
                    <button
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'rgba(255,255,255,0.9)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 10,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!user) {
                          setShowSignInModal(true);
                          return;
                        }
                        toggleSaveProduct(brandProduct.id ?? brandProduct.link);
                      }}
                      title={savedProducts.includes(normalizeProductId(brandProduct.id ?? brandProduct.link)) ? 'Remove from saved' : 'Save product'}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill={savedProducts.includes(normalizeProductId(brandProduct.id ?? brandProduct.link)) ? '#e63946' : 'none'}
                        stroke="#e63946"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 21s-7-4.35-7-10A5 5 0 0 1 12 6a5 5 0 0 1 7 5c0 5.65-7 10-7 10z" />
                      </svg>
                    </button>
                  </div>

                  {/* Product Info */}
                  <div style={{ textAlign: 'left' }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      fontFamily: 'Montserrat, sans-serif',
                      color: '#1a1a1a',
                      margin: '0 0 4px 0',
                      lineHeight: '1.2'
                    }}>
                      {brandProduct.brand || 'Unknown Brand'}
                    </h4>
                    <p style={{
                      fontSize: '12px',
                      fontFamily: 'Montserrat, sans-serif',
                      color: '#666',
                      margin: '0 0 4px 0',
                      lineHeight: '1.3',
                      height: '32px',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {capitalizeTitle(brandProduct.title) || 'Unnamed Product'}
                    </p>
                    <p style={{
                      fontSize: '13px',
                      fontFamily: 'Montserrat, sans-serif',
                      color: '#1a1a1a',
                      margin: 0,
                      fontWeight: '500'
                    }}>
                      {getCurrencySymbol()} {brandProduct.price ? Number(brandProduct.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </ScrollFadeIn>
        )}

        {/* TryOn Modal */}
        {tryOnOpen && user && (
          <TryOn
            productImage={tryOnImage}
            onClose={() => {
              setTryOnOpen(false);
              setTryOnImage(null);
            }}
            user={user}
          />
        )}

        {/* Sign In Modal */}
        <SignInModal
          open={showSignInModal}
          onClose={() => {
            setShowSignInModal(false);
            setAuthMessage('');
            setAuthEmail('');
            setSignInModalMessage('');
          }}
          message={signInModalMessage}
          authEmail={authEmail}
          setAuthEmail={setAuthEmail}
          authLoading={authLoading}
          authMessage={authMessage}
          onEmailSignIn={handleEmailSignIn}
          onGoogleSignIn={handleGoogleSignIn}
          onEmailPasswordSignIn={handleEmailPasswordSignIn}
          onEmailPasswordSignUp={handleEmailPasswordSignUp}
        />
      </Container>
    </>
  );
}

export default ProductDetail;
